<i18n>
  {
    "en": {
      "location": {
        "title": "Location",
        "tabs": {
          "location": "Location",
          "networks": "Networks",
          "floorPlan": "Floor Plan"
        },
        "deleteLocation": "Attention! The location and all related information will be deleted. This operation cannot be undone. Confirm the deletion."
      }
    },
    "ru": {
      "location": {
        "title": "Локация",
        "tabs": {
          "location": "Локация",
          "networks": "Сети",
          "floorPlan": "План помещения"
        },
        "deleteLocation": "Внимание! Локация и вся связанная с ней информация будет удалена. Эту операцию невозможно отменить. Подтвердите удаление."
      } 
    }
  }
</i18n>

<template>
  <div>
    <div class="content-block dx-card responsive-paddings">
      <DxValidationGroup ref="targetGroup">
        <dx-tab-panel
          :data-source="tabViews"
          :selected-index.sync="selectedIndex"
          :loop="false"
          :disabled="load || saving || deleting"
          :animation-enabled="true"
        >
          <template #item="view">
            <div class="tabpanel-item">
              <component
                v-bind:is="view.data.id"
                ref="form"
                :locationId="locationId"
                :dataSource="formData"
                :locationsItems="userLocations"
                :agentsItems="agents"
                :locationDictionaryChild="locationDictionaryChild"
                :statisticsDevices="statisticsDevices"
                @loading="loaded"
                @update-device="changedPositionDevices"
              />
            </div>
          </template>
        </dx-tab-panel>
      </DxValidationGroup>

      <div
        v-if="hasEditPermissions && selectedIndex === 0"
        class="app-form-buttons"
      >
        <active-button
          icon="save"
          type="default"
          :text="$t('general.save')"
          :busy="saving"
          :disabled="load || saving || deleting"
          @click="onSave"
        />

        <active-button
          icon="trash"
          type="danger"
          :text="$t('general.delete')"
          :busy="deleting"
          :disabled="isNew || load || saving || deleting"
          @click="onDelete"
        />
      </div>
    </div>
  </div>
</template>

<script>
import baseView from "../base.vue";
import appInfo from "../../app-info";

import DxTabPanel from "devextreme-vue/tab-panel";
import DxValidationGroup from 'devextreme-vue/validation-group';

import { notifier } from '@/shared/lib/notifier/index';

import ActiveButton from '@/components/active-button.vue';
import showConfirmDialog from '@/components/confirm-dialog';

import LocationDetail from "./detail.vue";
import LocationNetworks from "./networks";
import LocationFloorPlan from "./floor-plan.vue";
import { DataLocation } from "@/mixins/dataLocationMixin";

import {
  getUserLocations, getDictionaryItem, findDictionaries, getDictionaryItemAttributes, getLocationAgents, getStatisticsLocationsDevices, getDictionaryAttributes,
  putDictionaryItem, putDictionaryItemSystemAttributes, postDictionaryItem, deleteDictionaryItem
} from '@/services/api';
import { AclHandler } from "@/services/acl-handler";

import { cloneDeep, findIndex, find } from 'lodash';


const defaultAtrtributes = {
  '__ADDRESS_INDEX': { dateValue: null, flagValue: null, numericValue: null, stringValue: null },
  '__ADDRESS_CITY': { dateValue: null, flagValue: null, numericValue: null, stringValue: null },
  '__ADDRESS_STREET': { dateValue: null, flagValue: null, numericValue: null, stringValue: null },
  '__ADDRESS_HOUSE': { dateValue: null, flagValue: null, numericValue: null, stringValue: null },
  '__ADDRESS_FLOOR': { dateValue: null, flagValue: null, numericValue: null, stringValue: null },
  '__ADDRESS_ROOM': { dateValue: null, flagValue: null, numericValue: null, stringValue: null },
  '__CONTACT_NAME': { dateValue: null, flagValue: null, numericValue: null, stringValue: null },
  '__CONTACT_POSITION': { dateValue: null, flagValue: null, numericValue: null, stringValue: null },
  '__CONTACT_PHONES': { dateValue: null, flagValue: null, numericValue: null, stringValue: null },
  '__DESCRIPTION': { dateValue: null, flagValue: null, numericValue: null, stringValue: null }
};
const defaultData = {
  location: { code: '', name: '' },
  attributes: defaultAtrtributes,
  floorPlan: {
    attr: null,
    fileUploaded: null,
    imageFloorUrl: null,
    updateDevicesCoordinates: false,
  }
};

export default {
  props: ["id"],
  extends: baseView,
  mixins: [DataLocation],
  components: {
    LocationDetail, LocationNetworks, ActiveButton, LocationFloorPlan, DxTabPanel, DxValidationGroup
  },
  data() {
    return {
      isCloudMode: this.$store.getters.isCloudMode,
      formData: cloneDeep(defaultData),
      userLocations: [],
      locationDictionaryChild: [],
      agents: [],
      statisticsDevices: {},
      attributesValues: [],
      attributes: [],
      selectedIndex: 0,
      load: true,
      saving: false,
      deleting: false,
    };
  },
  computed: {
    locationId() {
      return Number(this.$route.params.id);
    },
    isNew() {
      return this.locationId < 0;
    },
    appUser() {
      return this.$store.getters.user;
    },
    aclUser() {
      return new AclHandler(this.appUser, this.isCloudMode);
    },
    isAdmin() {
      return this.aclUser.hasPermission({ roleCodes : ['ADMIN', 'DEVELOPER'] });
    },
    hasEditPermissions() {
      return this.aclUser.hasGrantForOperation('PAGE_LOCATION', 'EDIT') || false;
    },
    tabViews() {
      const tabs = [{
        id: "location-detail",
        title: this.$t("location.tabs.location"),
      }, {
        id: "location-floor-plan",
        title: this.$t("location.tabs.floorPlan"),
        disabled: this.isNew
      }, {
        id: "location-networks",
        title: this.$t("location.tabs.networks"),
        disabled: this.isNew
      }];

      if (this.isAdmin) {
        return tabs;
      } else {
        tabs.pop();
        return tabs;
      }
    },
    validationGroup() {
      return this.$refs['targetGroup'].instance;
    },
  },
  methods: {
    updateBreadcrumb() {
      let breadcrumb = [{
        link: '/locations',
        title: this.$t('menu.locations')
      }];

      if (!this.isNew) {
        let fullPathLocation = this.getFullPath(Number(this.locationId));
        fullPathLocation = fullPathLocation.map((location) => {
          return {
            link: `/location/${location.id}`,
            title: location.name
          };
        });

        breadcrumb = breadcrumb.concat(fullPathLocation);
      } else {
        breadcrumb.push({
          link: '',
          title: this.$t('locations.newItem.name')
        });
      }

      this.$store.commit('setBreadcrumb', breadcrumb);
    },
    async loadLocation() {
      try {
        const userLocations = await getUserLocations();
        this.locations = userLocations;
        userLocations.forEach(el => {
          if (el.parent) {
            if (findIndex(userLocations, ['code', el.parent.code]) === -1 ) {
              el.parent = null;
            }
          }
        });
        this.userLocations = userLocations;

        if (!this.isNew) {
          this.formData.location = await getDictionaryItem(this.locationId);

          const attributesValues = await getDictionaryItemAttributes(this.locationId);
          this.attributesValues = attributesValues.filter(item => item.attribute.code.substr(0, 2) === '__' && item.attribute.code !== '__FLOOR_PLAN');

          this.attributesValues.forEach(item => {
            if (item.attribute.code.substr(0, 2) === '__') {
              delete item.value;
              delete item.valueStr;
              this.formData.attributes[item.attribute.code] = item;
            }
          });

          this.agents = await getLocationAgents(this.locationId);
          this.statisticsDevices = await getStatisticsLocationsDevices(this.locationId);
          this.locationDictionaryChild = this.locations.filter((item) => item.parent && item.parent.id == this.locationId);

          this.formData.floorPlan.attr = find(attributesValues, (item) => item.attribute.code === '__FLOOR_PLAN');
        } else {
          this.formData.location.code = this.$t('locations.newItem.code');
          this.formData.location.name = this.$t('locations.newItem.name');

          const dictionaries = await findDictionaries({ page: '1,1', filterJson: ['code', '=', 'LOCATION'] });
          const locationsDictionariy = dictionaries.pageRows[0];

          this.formData.location.dictionary = locationsDictionariy;

          const attributes = await getDictionaryAttributes(locationsDictionariy.id);
          this.attributes = attributes.filter(item => item.code.substr(0, 2) === '__' && item.code !== '__FLOOR_PLAN');
          this.attributes.forEach(item => {
            this.formData.attributes[item.code] = {...this.formData.attributes[item.code], ...{
              "attribute": item,
              "dictionaryItem": this.formData.location
            }};
          });
          
          const floorPlanAttr = find(attributes, (item) => item.code === '__FLOOR_PLAN')
          this.formData.floorPlan.attr = {
            "attribute": floorPlanAttr,
            "dictionaryItem": this.formData.location
          };
        }
      } catch(err) {
        this.onDataError(err);
        // this.$router.push({name: "locations"});
      }

      this.updateBreadcrumb();
    },
    validate() {
      return this.validationGroup.validate();
    },
    async onSave() {
      if (!this.validate().isValid) {
        return;
      }

      this.saving = true;
      try {
        if (!this.isNew) {
          // обновление локации
          await putDictionaryItem(this.locationId, this.formData.location);

          // обновление атрибутов
          const locationItemSystemAttributes = Object.values(this.formData.attributes).filter(el => el.attribute.code !== '__FLOOR_PLAN');
          await putDictionaryItemSystemAttributes(this.locationId, locationItemSystemAttributes);
        }
        else {
          // создание локации
          const locationItem = await postDictionaryItem(this.formData.location);

          // обновление атрибутов
          const locationItemSystemAttributes = Object.values(this.formData.attributes).filter(el => el.attribute.code !== '__FLOOR_PLAN');
          await putDictionaryItemSystemAttributes(locationItem.id, locationItemSystemAttributes);

          this.$router.push({
            name: 'location',
            params: { id: locationItem.id }
          });
        }

        notifier.showSuccess(this.$t('general.updateSuccess'));
      } catch(err) {
        this.onDataError(err);
      }

      this.saving = false;
    },
    async onDelete() {
      const response = await showConfirmDialog({
        title: this.$t('general.delete'),
        message: this.$t('location.deleteLocation'),
        btnType: 'default',
        okButton: this.$t('general.proceed'),
        cancelButton: this.$t('general.cancel'),
      });

      if (!response) return;

      this.deleting = true;
      try {
        await deleteDictionaryItem(this.locationId);
        notifier.showSuccess(this.$t('general.deleteSuccess'));
        this.$router.push({ name: 'locations' });
      } catch(err) {
        this.onDataError(err);
      }
      this.deleting = false;
    },
    loaded(val) {
      this.load = val;
    },
    changedPositionDevices(devices) {
      this.formData.floorPlan.devices = devices;
    }
  },
  created() {
    document.title = `${appInfo.title} - ${this.$t('location.title')}`;
  },
  mounted() {
    this.loadLocation();
  },
  beforeDestroy() {
    this.formData = cloneDeep(defaultData);
  },
  watch: {
    id() {
      this.attributes = null;
      this.agents = [];
      this.locationDictionaryChild = [];
      this.selectedIndex = 0;

      this.loadLocation();
    }
  },
};
</script>
  
<style lang="scss" scoped>
.app-form-buttons {
  margin-top: 14px;
}
</style>